import { Component, type ErrorInfo, type ReactNode } from "react";
import { APP_VERSION } from "@shared/version";

const breadcrumbs: string[] = [];
const MAX_BREADCRUMBS = 10;

export function pushBreadcrumb(entry: string) {
  breadcrumbs.push(`${new Date().toISOString().slice(11, 19)} ${entry}`);
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

if (typeof window !== "undefined") {
  let lastPath = window.location.pathname + window.location.search;
  pushBreadcrumb(`navigate ${lastPath}`);
  const origPush = history.pushState;
  history.pushState = function (...args) {
    const r = origPush.apply(this, args as any);
    const next = window.location.pathname + window.location.search;
    if (next !== lastPath) {
      lastPath = next;
      pushBreadcrumb(`navigate ${next}`);
    }
    return r;
  };
  window.addEventListener("popstate", () => {
    const next = window.location.pathname + window.location.search;
    if (next !== lastPath) {
      lastPath = next;
      pushBreadcrumb(`navigate ${next}`);
    }
  });
  window.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    const tid = target?.closest?.("[data-testid]")?.getAttribute("data-testid");
    if (tid) pushBreadcrumb(`click ${tid}`);
  }, { capture: true, passive: true });
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

let lastReportAt = 0;
const REPORT_THROTTLE_MS = 2000;

export function reportClientError(payload: {
  message: string;
  errorStack?: string | null;
  componentStack?: string | null;
  kind: "react" | "windowerror" | "unhandledrejection";
}) {
  const now = Date.now();
  if (now - lastReportAt < REPORT_THROTTLE_MS) return;
  lastReportAt = now;

  const csrf = readCookie("sdf.csrf");
  const body = JSON.stringify({
    kind: payload.kind,
    message: payload.message,
    errorStack: payload.errorStack || null,
    componentStack: payload.componentStack || null,
    pageUrl: window.location.href,
    appVersion: APP_VERSION,
    breadcrumbs: breadcrumbs.slice(),
  });
  fetch("/api/system-logs/client-error", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(csrf ? { "x-csrf-token": csrf } : {}),
    },
    body,
    keepalive: true,
  }).catch(() => {});
}

if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    reportClientError({
      kind: "windowerror",
      message: e.message || "window.onerror",
      errorStack: e.error?.stack || null,
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason: any = e.reason;
    reportClientError({
      kind: "unhandledrejection",
      message: typeof reason === "string" ? reason : (reason?.message || "Unhandled promise rejection"),
      errorStack: reason?.stack || null,
    });
  });
}

interface State {
  hasError: boolean;
  message: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error?.message || "Unexpected error" };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError({
      kind: "react",
      message: error?.message || "React render error",
      errorStack: error?.stack || null,
      componentStack: info?.componentStack || null,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0a1628] p-6" data-testid="error-boundary-fallback">
          <div className="max-w-md w-full bg-[#0d1b3e] border border-white/10 rounded-lg p-6 text-center">
            <div className="text-red-400 text-sm font-semibold mb-2">Something went wrong</div>
            <div className="text-white/70 text-xs mb-4 font-mono break-words">{this.state.message}</div>
            <div className="text-white/40 text-sm mb-4">
              The error was reported to the System Diagnostics Log. IT can review it in Settings → System Logs.
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-cyan-600 hover:bg-cyan-700 text-white text-sm px-4 py-2 rounded-md"
              data-testid="button-error-reload"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
